import { useMemo, useState } from 'react'

import { EnhancedDimension, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { EmployeeForSelectFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import {
  EmployeeForSelectFragmentNoAccess,
  Groups,
} from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { GroupType } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

export type IdsTypes = number[] | (number | null | undefined)

interface Props {
  hideLabel?: boolean
  label?: string
  onSelectEmployee: (ids: IdsTypes) => void
  selectedEmployeeIds: number[]
  selectableEmployeeIds: number[]
  error?: string
  alwaysOpen?: boolean
  width?: EnhancedDimension | null
  defaultGroup?: GroupType
}

const EmployeesSelector = ({
  hideLabel,
  label,
  onSelectEmployee,
  selectedEmployeeIds,
  selectableEmployeeIds,
  alwaysOpen = false,
  width = null,
  defaultGroup = Groups.ALL,
}: Props) => {
  const initialSelectedEmployees: EmployeeForSelectFragment[] = useMemo(
    () =>
      selectedEmployeeIds.map((id) => ({
        id,
        firstName: '',
        fullName: `1 ${i18n.t('employee_selector.selected')}`,
        lastName: '',
        access: {
          id: 0,
        },
      })),
    [selectedEmployeeIds]
  )

  const [selectedEmployees, setSelectedEmployees] =
    useState<EmployeeForSelectFragmentNoAccess[]>(initialSelectedEmployees)

  const handleOnSelect = (employeesList: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(employeesList)
    onSelectEmployee(employeesList.map((employee) => employee.id))
  }

  useUnsafeEffect(() => {
    if (selectedEmployees.length != 0) return
    // Pre-select the employees when the component is mounted
    setSelectedEmployees(initialSelectedEmployees)
  }, [initialSelectedEmployees, selectedEmployees])

  return (
    <EmployeeSelector
      alwaysOpen={alwaysOpen}
      label={hideLabel ? '' : label || i18n.t('shift_management.no_selection_form.assign_to')}
      value={selectedEmployees}
      onSelect={handleOnSelect}
      hideSearchIcon
      withAvatar
      multiple
      width={width ?? undefined}
      allowedGroups={['all', 'teams', 'managers', 'workplaces']}
      defaultGroup={defaultGroup}
      employeeIds={selectableEmployeeIds}
    />
  )
}

export default observer(EmployeesSelector)
